import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getAppFieldSetting } from '../../services/api/configService';
import type { appFieldSettingResponse } from '../../types/appConfig';

export interface AppConfigState {
  /**
   * Keyed by `fieldEnumKey` (e.g. 2 for DOB, 16 for Gender) → isVisible.
   * Populated from the flat array returned by app/Masters/FieldSettings.
   */
  visibility: Record<number, boolean>;
  fields: Record<string, {
    isVisible: boolean;
    labelValueSingular?: string | null;
    labelValuePlural?: string | null;
    value?: string;
  }>;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AppConfigState = {
  visibility: {},
  fields: {},
  isLoaded: false,
  isLoading: false,
  error: null,
};

const normalizeConfigKey = (key: number | string | null | undefined): string | null => {
  if (key === null || key === undefined || key === '') {
    return null;
  }

  return String(key).trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
};

const compactConfigKey = (key: string): string => key.replace(/_/g, '');

export const fetchAppConfig = createAsyncThunk(
  'appConfig/fetch',
  async (_, { rejectWithValue }) => {
    try {
      return await getAppFieldSetting();
   
    } catch (error: any) {
      return rejectWithValue(error?.message ?? 'Failed to load app config');
    }
  }
);

const appConfigSlice = createSlice({
  name: 'appConfig',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppConfig.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchAppConfig.fulfilled,
        (state, action: PayloadAction<appFieldSettingResponse>) => {
          state.isLoading = false;
          state.isLoaded = true;

          const visibility: Record<number, boolean> = {};
          const fields: AppConfigState['fields'] = {};

          for (const item of action.payload) {
            const field = {
              isVisible: item.isVisible,
              labelValuePlural: item.labelValuePlural || null,
              labelValueSingular: item.labelValueSingular || null,
              value: item.fieldValue || undefined,
            };
            const numericKey = Number(item.fieldEnumKey);

            if (Number.isFinite(numericKey)) {
              visibility[numericKey] = item.isVisible;
            }

            const enumKey = normalizeConfigKey(item.fieldEnumKey);
            const valueKey = normalizeConfigKey(item.fieldValue);

            for (const key of [enumKey, valueKey]) {
              if (!key) continue;
              fields[key] = field;
              fields[compactConfigKey(key)] = field;
            }
          }

          state.visibility = visibility;
         
          state.fields = fields;
        }
      )
      .addCase(fetchAppConfig.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default appConfigSlice.reducer;
